import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import Select from "react-select";
import { Printer, Search } from "react-feather";
import { FileExcel } from "react-bootstrap-icons";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { ErrorHandler } from "../../../common/utils/Error";
import axios from "axios";
import { formatCurrency } from "../../../utility/Utils";
import moment from "moment";

import LetterHead from "@src/assets/images/letterhead/LetterHead-04.png";
import Icon from "@mdi/react";
import { mdiMicrosoftExcel } from "@mdi/js";
import { useReactToPrint } from "react-to-print";
import { useRef } from "react";
import { downloadCSV } from "../../../utility/Utils";
import { Link } from "react-router-dom";

export default function BalanceSheetStatement() {
  let assets = [];
  let liabilities = [];
  let equity = [];
  let netIncome = 0;
  const [nodes, setNodes] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultValues = { fiscalYear: "", start_date: "", end_date: "" };

  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues });

  const onSubmit = (values) => {
    values.fiscalYear = values.fiscalYear ? values.fiscalYear.value : "";
    let q = "?";
    for (const key in values) {
      if (values[key] != "") {
        q += `&${key}=${values[key]}`;
      }
    }

    getStatement(q.length > 1 ? q : "");
  };

  const getStatement = async (query) => {
    try {
      setLoading(true);
      const { data, status } = await axios.get(
        `/accountings/statements/balancesheet${query}`
      );

      if (status == 200) {
        assets = data.data.assets;
        liabilities = data.data.liability;
        equity = data.data.equity;
        netIncome = data.data.netIncome;
      }
      formatTreeNodes();
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([getStatement(""), getFiscalYears()]);
  }, []);

  useEffect(() => {
    expandAll();
  }, [nodes]);

  const formatTreeNodes = () => {
    let assetsAcc = [];
    let liabilityAcc = [];
    let equityAcc = [];

    let totalAsset = assets.reduce(
      (prev, rvacc) => prev + Number(rvacc.balance),
      0
    );

    let totalLiability = liabilities.reduce(
      (prev, rvacc) => prev + Number(rvacc.balance),
      0
    );
    let totalEquity = equity.reduce(
      (prev, rvacc) => prev + Number(rvacc.balance),
      0
    );

    totalEquity += netIncome;

    assets
      .filter((acc) => acc.parentAccount == null && acc.type == null)
      .forEach((account) => {
        let subaccounts = getSubAccounts(assets, account._id);
        let collapseBalance = subaccounts.reduce(
          (prev, sub) =>
            prev + Number(sub.data.balance) + Number(sub.collapseBalance),
          0
        );

        assetsAcc.push({
          key: account._id,
          isTitle: account.type == null,
          collapseBalance,
          data: { ...account },
          children: subaccounts,
        });
      });

    liabilities
      .filter((acc) => acc.parentAccount == null && acc.type == null)
      .forEach((account) => {
        let subaccounts = getSubAccounts(liabilities, account._id);
        let collapseBalance = subaccounts.reduce(
          (prev, sub) =>
            prev + Number(sub.data.balance) + Number(sub.collapseBalance),
          0
        );
        liabilityAcc.push({
          key: account._id,
          isTitle: account.type == null,
          collapseBalance,
          data: { ...account, balance: account.balance },
          children: subaccounts,
        });
      });

    equity
      .filter((acc) => acc.parentAccount == null && acc.type == null)
      .forEach((account) => {
        let subaccounts = getSubAccounts(equity, account._id);
        let collapseBalance = subaccounts.reduce(
          (prev, sub) =>
            prev + Number(sub.data.balance) + Number(sub.collapseBalance),
          0
        );
        equityAcc.push({
          key: account._id,
          isTitle: account.type == null,
          collapseBalance,
          data: { ...account, balance: account.balance },
          children: subaccounts,
        });
      });
    equityAcc.push({
      key: `row_` + Math.floor(Math.random() * 100000),
      isNetIncome: true,
      data: {
        name: netIncome > 0 ? "NET PROFIT" : "NET LOSS",
        balance: netIncome,
      },
    });
    let acc = [
      ...assetsAcc,
      {
        key: `row_` + Math.floor(Math.random() * 10000),
        isGrandTotal: true,
        data: {
          name: "Total Assets",
          balance: totalAsset,
        },
      },
      ...liabilityAcc,
      {
        key: `row_` + Math.floor(Math.random() * 10000),
        isTotal: true,
        data: {
          name: "Total Liability",
          balance: totalLiability,
        },
      },
      ...equityAcc,

      {
        key: `row_` + Math.floor(Math.random() * 10000),
        isTotal: true,
        data: {
          name: "Total Equity",
          balance: totalEquity,
        },
      },

      {
        key: `row_` + Math.floor(Math.random() * 10000),
        isGrandTotal: true,
        data: {
          name: "Total Liability + Total Equity",
          balance: totalLiability + totalEquity,
        },
      },
    ];

    setNodes(acc);

    return acc;
  };

  const getSubAccounts = (data, parent) => {
    return data
      .filter((account) => {
        if (account.parentAccount == null && account.type == parent) {
          return true;
        } else if (account.parentAccount != null && account.type == parent) {
          return false;
        } else if (account.parentAccount == parent || account.type == parent) {
          return true;
        }
      })
      .map((account) => {
        let subaccounts = getSubAccounts(data, account._id);
        let collapseBalance = subaccounts.reduce(
          (prev, sub) =>
            prev + (Number(sub.collapseBalance) + Number(sub.data?.balance)),
          0
        );
        return {
          key: account._id,
          isExpanded:
            expandedKeys && account._id in expandedKeys == true ? true : null,
          collapseBalance: collapseBalance,
          data: { ...account },
          children: subaccounts,
        };
      });
  };

  const rowClassName = (node) => {
    return {
      "fw-bolder bg-light": node.isTitle,
      "fw-bolder text-primary ": node.isTotal,
      "fw-bolder text-success ": node.isProit == true,
      "fw-bolder text-danger ": node.isProit == false,
      "fw-bolder text-white bg-dark": node.isGrandTotal,
    };
  };

  const columStyle = (node) => {
    let data = node.data;
    let fontWeight = node.isProit ? "900" : "";

    if (node?.children?.length) {
      if (expandedKeys != null && expandedKeys[data._id] == true) {
        return (
          <span style={{ fontWeight: fontWeight }}>
            {data.systemAccount != true ? formatCurrency(data.balance) : "---"}
          </span>
        );
      } else {
        return (
          <span style={{ fontWeight: fontWeight }}>
            {formatCurrency(node.collapseBalance + data.balance)}
          </span>
        );
      }
    }

    return (
      <span style={{ fontWeight: fontWeight }}>
        {data.systemAccount != true ? formatCurrency(data.balance) : "---"}
      </span>
    );
  };

  const AccountStyle = (node) => {
    let data = node.data;
    if (
      node.isGrandTotal ||
      node.isProit ||
      node.isTotal ||
      node.isTitle ||
      node.isNetIncome
    ) {
      return <span>{data.name}</span>;
    }

    return (
      <Link to={"/accountings/account-statement?account=" + data._id}>
        <span>{data?.name}</span>
      </Link>
    );
  };

  const expandAll = () => {
    let _expandedKeys = {};

    for (let node of nodes) {
      expandNode(node, _expandedKeys);
    }

    setExpandedKeys(_expandedKeys);
  };

  const expandNode = (node, _expandedKeys) => {
    if (node.children && node.children.length) {
      _expandedKeys[node.key] = true;

      for (let child of node.children) {
        expandNode(child, _expandedKeys);
      }
    }
  };

  const getFiscalYears = async () => {
    try {
      var { status, data } = await axios.get("/accountings/books/list");
      if (status == 200) {
        setFiscalYears(data?.data ?? []);
        let curr = data.data.find((year) => year.isCurrent == true);
        if (curr) {
          setValue("fiscalYear", { label: curr.name, value: curr._id });
        }
      }
    } catch (error) {
      ErrorHandler(error);
    }
  };

  const printRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Balance Sheet Report`,
    pageStyle: `
    
    @media print {
      html, body {
        height: initial !important;
        overflow: initial !important;
        -webkit-print-color-adjust: exact;
      }
    }
    
    @media print {
      .page-break {
        margin-top: 1rem;
        display: block;
        page-break-before: always !important;
      }
      .card{
        box-shadow:none !important;
      }
    }
    
    @page {
      size: auto;
    }
    `,
    bodyClass: "",
    // removeAfterPrint: true,
  });

  return (
    <>
      <Card>
        <CardBody className="d-flex align-items-center justify-content-between">
          <h4 className="mb-0">Balance Sheet Statement</h4>
        </CardBody>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col xs={12} md={4}>
                <Label className="form-label" for={`fiscal_year`}>
                  Fiscal Year
                </Label>
                <Controller
                  name={`fiscalYear`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      id={`fiscal_year`}
                      className="react-select"
                      classNamePrefix="select"
                      // invalid={errors.lines![index].account && true}
                      {...field}
                      placeholder={"Select Fiscal Year"}
                      options={fiscalYears?.map((year) => ({
                        label: year.name,
                        value: year._id,
                      }))}
                    />
                  )}
                />
                {/* {errors.name && (
                    <FormFeedback>{JSON.stringify(errors.name)}</FormFeedback>
                  )} */}
              </Col>
              <Col xs={12} md={3}>
                <Label className="form-label" for={`start_date`}>
                  From Date
                </Label>
                <Controller
                  name={`start_date`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={`start_date`}
                      invalid={errors.dateRange && true}
                      {...field}
                      type="date"
                    />
                  )}
                />
                {/* {errors.name && (
                    <FormFeedback>{JSON.stringify(errors.name)}</FormFeedback>
                  )} */}
              </Col>
              <Col xs={12} md={3}>
                <Label className="form-label" for={`end_date`}>
                  To Date
                </Label>
                <Controller
                  name={`end_date`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={`end_date`}
                      invalid={errors.dateRange && true}
                      {...field}
                      type="date"
                    />
                  )}
                />
                {/* {errors.name && (
                    <FormFeedback>{JSON.stringify(errors.name)}</FormFeedback>
                  )} */}
              </Col>

              <Col xs={12} md={2} className="text-center mt-2">
                <Button className="h-100 w-100" color="primary">
                  {" "}
                  <Search size={14} className="" /> Apply Filter{" "}
                </Button>
              </Col>
            </Row>
          </form>
        </CardBody>
      </Card>
      <Card innerRef={printRef} dir="ltr">
        <CardBody>
          <div className="d-flex justify-content-between">
            <div>
              <img src={LetterHead} alt="" className="mb-1" width={70} />

              <h2 className="mb-0">Balance Sheet</h2>
              <p className="mb-2">
                {getValues().start_date ? (
                  <>
                    {moment(getValues().start_date).format("DD/MMMM/YYYY")}
                    <span className="mx-2">to</span>
                    {getValues().end_date
                      ? moment(getValues().end_date).format("DD/MMMM/YYYY")
                      : "Today"}
                  </>
                ) : (
                  "All Date"
                )}
              </p>
            </div>
            <div className="no-print">
              <Printer
                size={26}
                className="cursor-pointer me-1"
                title="Print"
                onClick={() => handlePrint()}
              />
              <span
                className="cursor-pointer"
                onClick={() => {
                  let excl = nodes.map((node) => node.data);
                  downloadCSV(excl, "Balance Sheet");
                }}
              >
                <Icon path={mdiMicrosoftExcel} size={1.6} horizontal />
              </span>
            </div>
          </div>
          {loading ? (
            <div className="d-flex justify-content-center">
              <Spinner />
            </div>
          ) : (
            <TreeTable
              value={nodes}
              expandedKeys={expandedKeys}
              onToggle={(e) => setExpandedKeys(e.value)}
              emptyMessage="Balance sheet Not Available"
              rowClassName={rowClassName}
              className="border"
              tableClassName="table"
            >
              <Column
                field="name"
                header="Account"
                hidden={true}
                expander
                body={AccountStyle}
              ></Column>
              <Column
                field="balance"
                header="Total"
                hidden={true}
                body={columStyle}
              ></Column>
            </TreeTable>
          )}
        </CardBody>
      </Card>
    </>
  );
}
